import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { ViolationsScope } from "./api/network/violations";

/**
 * URL-driven scope filter for the Insights workspace. All tabs read the same
 * ``?from=&to=&device_group=&platform=`` query state via this hook, so the
 * scope filter at the top of the workspace is shared and shareable.
 *
 * Date range presets are pure helpers — they don't get persisted; the URL
 * always carries explicit ``from``/``to`` ISO strings once the user picks one.
 */
export interface InsightsScope extends ViolationsScope {
  from?: string;
  to?: string;
  device_group?: string;
  platform?: "android" | "ios" | string;
}

function isoOffsetDays(daysAgo: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}

function endOfTodayIso(): string {
  const d = new Date();
  d.setUTCHours(23, 59, 59, 999);
  return d.toISOString();
}

export const SCOPE_PRESETS: { key: string; days: number }[] = [
  { key: "24h", days: 1 },
  { key: "7d", days: 7 },
  { key: "30d", days: 30 },
  { key: "90d", days: 90 },
];

export function presetRange(days: number): { from: string; to: string } {
  return { from: isoOffsetDays(days - 1), to: endOfTodayIso() };
}

export function useInsightsScope(): InsightsScope {
  const [params] = useSearchParams();
  return useMemo(
    () => ({
      from: params.get("from") || undefined,
      to: params.get("to") || undefined,
      device_group: params.get("device_group") || undefined,
      platform: (params.get("platform") as "android" | "ios" | undefined) || undefined,
    }),
    [params],
  );
}
